/**
 * Wall installation plan — single A4 (or A3) landscape page with computed scale ratio.
 * Renders REAL letter outlines from the font + drilling holes per segment.
 * Defensive: every coordinate is validated to avoid "Invalid argument passed to jsPDF.scale".
 */
import jsPDF from 'jspdf';
import { FontLoader } from 'three/examples/jsm/loaders/FontLoader.js';

const FONT_URL = '/fonts/helvetiker_bold.typeface.json';
let _fontPromise = null;
function getFont() {
  if (!_fontPromise) {
    _fontPromise = new Promise((resolve, reject) => {
      const loader = new FontLoader();
      loader.load(FONT_URL, resolve, undefined, reject);
    });
  }
  return _fontPromise;
}

const A4_LANDSCAPE = { w: 297, h: 210 };
const A3_LANDSCAPE = { w: 420, h: 297 };

// ---------- safety helpers ----------
const num = (v, fb = 0) => (Number.isFinite(v) ? v : fb);
const isFiniteNum = (v) => Number.isFinite(v);

function hexToRgb(hex) {
  if (typeof hex !== 'string') return [0, 0, 0];
  const h = hex.replace('#', '');
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return [
    Number.isFinite(r) ? r : 0,
    Number.isFinite(g) ? g : 0,
    Number.isFinite(b) ? b : 0,
  ];
}

// Draw a closed polyline safely using doc.line() (avoids doc.lines() edge-cases).
function drawPolyline(doc, points, ox, oy, scale, color) {
  if (!Array.isArray(points) || points.length < 2) return;
  const [r, g, b] = hexToRgb(color);
  doc.setDrawColor(r, g, b);
  doc.setLineWidth(0.25);

  const finiteScale = isFiniteNum(scale) && scale > 0 ? scale : 1;
  const finiteOx = num(ox);
  const finiteOy = num(oy);

  const proj = points
    .map((p) => {
      if (!p || !isFiniteNum(p.x) || !isFiniteNum(p.y)) return null;
      return {
        x: finiteOx + p.x * finiteScale,
        y: finiteOy - p.y * finiteScale, // flip Y: font Y up, PDF Y down
      };
    })
    .filter(Boolean);

  if (proj.length < 2) return;
  for (let i = 1; i < proj.length; i++) {
    const a = proj[i - 1];
    const b2 = proj[i];
    if (isFiniteNum(a.x) && isFiniteNum(a.y) && isFiniteNum(b2.x) && isFiniteNum(b2.y)) {
      doc.line(a.x, a.y, b2.x, b2.y);
    }
  }
  // Close the loop
  const first = proj[0];
  const last = proj[proj.length - 1];
  if (first && last && (first.x !== last.x || first.y !== last.y)) {
    doc.line(last.x, last.y, first.x, first.y);
  }
}

function drawLetterOutline(doc, shape, offsetX, offsetY, scale, color = '#000000') {
  if (!shape || typeof shape.getPoints !== 'function') return;
  const outer = shape.getPoints(28);
  drawPolyline(doc, outer, offsetX, offsetY, scale, color);
  // Inner counters (holes of A / O / e / etc.)
  const holes = Array.isArray(shape.holes) ? shape.holes : [];
  holes.forEach((h) => {
    if (h && typeof h.getPoints === 'function') {
      drawPolyline(doc, h.getPoints(28), offsetX, offsetY, scale, color);
    }
  });
}

function segmentBoundsMm(font, text, heightMm) {
  const safeText = typeof text === 'string' && text.length > 0 ? text : ' ';
  const safeH = isFiniteNum(heightMm) && heightMm > 0 ? heightMm : 150;
  let shapes = [];
  try {
    shapes = font.generateShapes(safeText, safeH) || [];
  } catch {
    shapes = [];
  }
  let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
  shapes.forEach((s) => {
    try {
      s.getPoints(14).forEach((p) => {
        if (!p) return;
        if (isFiniteNum(p.x)) {
          if (p.x < minX) minX = p.x;
          if (p.x > maxX) maxX = p.x;
        }
        if (isFiniteNum(p.y)) {
          if (p.y < minY) minY = p.y;
          if (p.y > maxY) maxY = p.y;
        }
      });
    } catch { /* ignore glyph */ }
  });
  const width = isFinite(maxX - minX) && maxX > minX
    ? (maxX - minX)
    : Math.max(1, safeH * 0.6 * safeText.length);
  const height = isFinite(maxY - minY) && maxY > minY
    ? (maxY - minY)
    : safeH;
  return {
    shapes,
    width: num(width, safeH),
    height: num(height, safeH),
    minX: isFinite(minX) ? minX : 0,
    minY: isFinite(minY) ? minY : 0,
  };
}

export async function exportPosePlanA4(state) {
  const { segments, dimensions, standoffs, meta } = state || {};
  if (!Array.isArray(segments) || segments.length === 0) {
    throw new Error('Aucun segment à tracer');
  }
  const font = await getFont();
  const heightMm = num(dimensions?.height, 150);

  // Per-segment bounds
  const segData = segments.map((seg) => {
    const b = segmentBoundsMm(font, seg?.text, heightMm);
    return { seg: seg || {}, ...b };
  });

  const gapDefault = 15;
  let cursor = 0;
  const positioned = segData.map((sd) => {
    const x = cursor;
    const gap = sd.seg?.detached ? num(sd.seg?.gapAfterMm, 30) : gapDefault;
    cursor += num(sd.width, heightMm) + gap;
    return { ...sd, x };
  });
  const totalWidthMm = Math.max(1, cursor - gapDefault);

  // Auto page format
  const usableA4W = A4_LANDSCAPE.w - 30;
  const usableA4H = A4_LANDSCAPE.h - 60;
  const scaleA4 = Math.min(
    usableA4W / (totalWidthMm + 20),
    usableA4H / (heightMm + 30),
  );
  let pageFmt = 'a4';
  let page = A4_LANDSCAPE;
  if (!isFiniteNum(scaleA4) || scaleA4 < 0.06) {
    pageFmt = 'a3';
    page = A3_LANDSCAPE;
  }
  const usableW = page.w - 30;
  const usableH = page.h - 70;
  let scale = Math.min(
    usableW / (totalWidthMm + 20),
    usableH / (heightMm + 30),
  );
  if (!isFiniteNum(scale) || scale <= 0) scale = 0.1;
  const ratio = Math.max(1, Math.round(1 / scale));

  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: pageFmt });

  // Header
  doc.setFillColor(20, 20, 24);
  doc.rect(0, 0, page.w, 24, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(14); doc.setFont('helvetica', 'bold');
  doc.text('PLAN DE POSE — REPÈRES DE PERÇAGE', 12, 10);
  doc.setFontSize(9); doc.setFont('helvetica', 'normal');
  doc.text(
    `Projet : ${meta?.name || 'Enseigne'}   |   Client : ${meta?.client || '-'}   |   ${meta?.installAddress || ''}`,
    12, 16,
  );
  doc.setFont('helvetica', 'bold');
  doc.text(`ÉCHELLE 1 : ${ratio}`, page.w - 12, 10, { align: 'right' });
  doc.setFont('helvetica', 'normal');
  doc.text(`Envelope : ${Math.round(totalWidthMm)} × ${Math.round(heightMm)} mm`, page.w - 12, 16, { align: 'right' });

  // Origin
  const drawW = totalWidthMm * scale;
  const drawH = heightMm * scale;
  const originX = num((page.w - drawW) / 2, 15);
  const originY = num(40 + (usableH - drawH) / 2, 40);

  // Envelope box
  doc.setDrawColor(160);
  doc.setLineWidth(0.25);
  try { doc.setLineDashPattern([1.5, 1.5], 0); } catch { /* ignore */ }
  doc.rect(originX - 5, originY - 5, drawW + 10, drawH + 10, 'S');
  try { doc.setLineDashPattern([], 0); } catch { /* ignore */ }

  // Draw each segment's letter outlines + drilling markers
  positioned.forEach((sd, si) => {
    const segX = num(originX + sd.x * scale, originX);
    const segY = num(originY + drawH, originY); // baseline = envelope bottom
    doc.setDrawColor(0);
    doc.setLineWidth(0.25);
    (sd.shapes || []).forEach((shape) => {
      drawLetterOutline(doc, shape, segX, segY, scale, si === 0 ? '#000000' : '#3b82f6');
    });
    // Segment label
    doc.setTextColor(120, 120, 120);
    doc.setFontSize(7);
    const labelX = num(segX + (sd.width * scale) / 2, segX);
    doc.text(String(sd.seg?.text || `Segment ${si + 1}`), labelX, originY - 3, { align: 'center' });

    // Drilling markers for this segment
    // If seg.customHoles is a non-empty array, USE THEM (override) — otherwise fallback to
    // the automatic balanced layout below.
    // NOTE : dessiné INDÉPENDAMMENT de standoffs.enabled — le plan de pose sert justement
    // à la fixation murale même quand les entretoises ne sont pas simulées en 3D.
    {
      const segWmm = num(sd.width, 100);
      const custom = Array.isArray(sd.seg?.customHoles) ? sd.seg.customHoles : [];
      let holes;
      let perHoleDia = false;
      if (custom.length > 0) {
        holes = custom.map((h) => ({
          xMm: num(h.xMm, 0),
          yMm: num(h.yMm, 0),
          diameterMm: num(h.diameterMm, 6),
        }));
        perHoleDia = true;
      } else {
        // Balanced auto-layout
        const nStand = Math.max(2, num(standoffs?.count, 2));
        const marginRatio = 0.12;
        let rows = 1;
        let cols = nStand;
        if (nStand >= 4) { rows = 2; cols = Math.ceil(nStand / 2); }
        const xLeftMm = segWmm * marginRatio;
        const xRightMm = segWmm * (1 - marginRatio);
        const xs = [];
        for (let c = 0; c < cols; c++) {
          const t = cols === 1 ? 0.5 : c / (cols - 1);
          xs.push(xLeftMm + (xRightMm - xLeftMm) * t);
        }
        const ys = rows === 1 ? [heightMm * 0.5] : [heightMm * 0.78, heightMm * 0.22];
        holes = [];
        outer: for (let r = 0; r < rows; r++) {
          for (let c = 0; c < cols; c++) {
            holes.push({ xMm: xs[c], yMm: ys[r], diameterMm: num(standoffs?.diameter, 6) });
            if (holes.length >= nStand) break outer;
          }
        }
      }

      // Draw each hole with target cross + dimensions
      doc.setDrawColor(220, 30, 130);
      doc.setFillColor(220, 30, 130);
      doc.setLineWidth(0.35);
      holes.forEach((h, idx) => {
        const sxMmAbs = sd.x + h.xMm;
        const sx = num(originX + sxMmAbs * scale);
        const sy = num(originY + drawH - h.yMm * scale);
        const stDia = num(h.diameterMm, 6);
        const rDraw = Math.max(1.4, (stDia / 2) * scale);
        if (!isFiniteNum(sx) || !isFiniteNum(sy) || !isFiniteNum(rDraw)) return;
        doc.setDrawColor(220, 30, 130);
        doc.setFillColor(220, 30, 130);
        doc.setLineWidth(0.35);
        doc.circle(sx, sy, rDraw, 'FD');
        doc.setDrawColor(220, 30, 130);
        doc.setLineWidth(0.25);
        const armLen = rDraw * 2.6;
        doc.line(sx - armLen, sy, sx + armLen, sy);
        doc.line(sx, sy - armLen, sx, sy + armLen);
        doc.setTextColor(220, 30, 130);
        doc.setFontSize(6.5);
        doc.setFont('helvetica', 'bold');
        doc.text(`P${idx + 1}${perHoleDia ? `·Ø${Math.round(stDia)}` : ''}`, sx + armLen + 0.8, sy - 0.5);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(6);
        doc.text(`x=${Math.round(h.xMm)}`, sx, sy + rDraw + 3.2, { align: 'center' });
        doc.text(`y=${Math.round(h.yMm)}`, sx + armLen + 0.8, sy + 3.5);
      });

      // Symmetry axis (dashed vertical through segment centre) — helps installer verify balance
      const cxMm = sd.x + segWmm * 0.5;
      const cx = num(originX + cxMm * scale);
      const cyTop = originY;
      const cyBot = originY + drawH;
      doc.setDrawColor(200, 200, 210);
      doc.setLineWidth(0.15);
      try { doc.setLineDashPattern([0.8, 0.8], 0); } catch { /* ignore */ }
      doc.line(cx, cyTop - 2, cx, cyBot + 2);
      try { doc.setLineDashPattern([], 0); } catch { /* ignore */ }
    }
  });

  // ============================================================
  // ÉCHELLE / RÈGLE GRADUÉE — indispensable pour vérifier / imprimer à l'échelle
  // ============================================================
  // (A) Règle graduée à l'échelle du plan (représente 100 mm réels ⇒ 100*scale mm sur papier)
  //     Le poseur peut la mesurer avec sa règle et vérifier que l'impression n'a pas été
  //     déformée / mise à l'échelle par l'imprimante. Reporter chaque cote × ratio.
  const barMm = 100; // 100 mm à l'échelle
  const barLenPaper = barMm * scale; // longueur physique sur le PDF
  const barX = originX;
  const barY = num(originY + drawH + 10, page.h - 30);

  doc.setDrawColor(20, 20, 24);
  doc.setLineWidth(0.6);
  doc.line(barX, barY, barX + barLenPaper, barY);
  // Ticks tous les 10 mm à l'échelle
  const step = 10; // mm
  for (let m = 0; m <= barMm; m += step) {
    const tx = barX + m * scale;
    const th = (m % 50 === 0) ? 2.2 : (m % 10 === 0 ? 1.4 : 0.8);
    doc.setLineWidth(m % 50 === 0 ? 0.5 : 0.3);
    doc.line(tx, barY, tx, barY - th);
    if (m % 50 === 0) {
      doc.setFontSize(6); doc.setTextColor(20, 20, 24); doc.setFont('helvetica', 'normal');
      doc.text(`${m}`, tx, barY + 3.5, { align: 'center' });
    }
  }
  doc.setFontSize(7); doc.setFont('helvetica', 'bold'); doc.setTextColor(20, 20, 24);
  doc.text(`RÈGLE : 100 mm à l'échelle 1:${ratio}   (soit ${barLenPaper.toFixed(1)} mm sur ce papier)`, barX, barY + 8);
  doc.setFont('helvetica', 'normal');

  // (B) Trait de contrôle 1:1 — un segment de 50 mm imprimé à la taille réelle
  //     Après impression, le poseur mesure ce trait ; s'il fait 50 mm, l'impression est OK.
  //     Placé une ligne plus bas pour ne pas se chevaucher avec le suffixe de la règle.
  const ctrlX0 = barX;
  const ctrlY = barY + 16;
  const ctrl1to1 = 50; // 50 mm réels sur le papier (indépendant de "scale")
  if (ctrlX0 + ctrl1to1 + 10 < page.w - 10) {
    doc.setDrawColor(0, 130, 40);
    doc.setLineWidth(0.7);
    doc.line(ctrlX0, ctrlY, ctrlX0 + ctrl1to1, ctrlY);
    doc.line(ctrlX0, ctrlY - 2, ctrlX0, ctrlY + 2);
    doc.line(ctrlX0 + ctrl1to1, ctrlY - 2, ctrlX0 + ctrl1to1, ctrlY + 2);
    doc.setFontSize(7); doc.setFont('helvetica', 'bold'); doc.setTextColor(0, 130, 40);
    doc.text(`CONTRÔLE 1:1 — ce trait doit mesurer 50 mm après impression`, ctrlX0 + ctrl1to1 + 4, ctrlY + 1);
    doc.setFont('helvetica', 'normal');
  }

  // Procedure block
  let y = num(ctrlY + 10, page.h - 40);
  doc.setTextColor(30, 30, 30);
  doc.setFontSize(9); doc.setFont('helvetica', 'bold');
  doc.text('PROCÉDURE DE POSE :', 12, y); y += 5;
  doc.setFont('helvetica', 'normal');
  const nStand = Math.max(2, num(standoffs?.count, 2));
  const stD = num(standoffs?.diameter, 6);
  const stH = num(standoffs?.height, 25);
  const steps = [
    `1. Reporter chaque cote (x, y) du plan sur le mur en la multipliant par ${ratio} — ou utiliser la RÈGLE graduée en bas du plan comme référence 1:${ratio}. Utiliser un niveau à bulle.`,
    `2. Percer aux repères roses P1, P2… : Ø${stD} mm, profondeur ${Math.round(stH * 1.5)} mm. Contrôler la symétrie via l'axe pointillé de chaque élément.`,
    `3. Chevilles adaptées : M${Math.max(6, Math.round(stD * 0.5))} béton / MolyBolt placo / crochets bois.`,
    `4. Visser ${nStand} entretoise(s) par élément (${standoffs?.material === 'aluminum' ? 'aluminium' : 'inox'} Ø${stD}×${stH} mm).`,
    `5. Aligner les trous du fond du socle sur les entretoises et enclipser chaque élément.`,
    `6. Vérifier la planéité et serrer.`,
  ];
  steps.forEach((s) => { doc.setFontSize(8); doc.text(s, 12, y); y += 4; });

  // Legend (right column, aligned with procedure block)
  doc.setFontSize(8); doc.setFont('helvetica', 'bold');
  const legendY = num(ctrlY + 10, page.h - 40);
  doc.setTextColor(30, 30, 30);
  doc.text('LÉGENDE :', page.w - 60, legendY);
  doc.setFont('helvetica', 'normal');
  doc.setDrawColor(0); doc.setLineWidth(0.4);
  doc.line(page.w - 60, legendY + 4, page.w - 54, legendY + 4);
  doc.text('Contours à l\'échelle', page.w - 52, legendY + 5);
  doc.setDrawColor(220, 30, 130); doc.setFillColor(220, 30, 130);
  doc.circle(page.w - 57, legendY + 9, 1.2, 'F');
  doc.setTextColor(30, 30, 30);
  doc.text('Repères de perçage P1…Pn', page.w - 52, legendY + 10);
  doc.setDrawColor(160); doc.setLineWidth(0.25);
  try { doc.setLineDashPattern([1, 1], 0); } catch { /* ignore */ }
  doc.line(page.w - 60, legendY + 14, page.w - 54, legendY + 14);
  try { doc.setLineDashPattern([], 0); } catch { /* ignore */ }
  doc.text('Envelope + axe de symétrie', page.w - 52, legendY + 15);
  doc.setDrawColor(0, 130, 40); doc.setLineWidth(0.7);
  doc.line(page.w - 60, legendY + 19, page.w - 54, legendY + 19);
  doc.setTextColor(30, 30, 30);
  doc.text('Contrôle 1:1 (50 mm réels)', page.w - 52, legendY + 20);

  // Footer
  doc.setDrawColor(20);
  doc.line(0, page.h - 8, page.w, page.h - 8);
  doc.setFontSize(7); doc.setTextColor(120, 120, 120);
  doc.text(`Généré le ${new Date().toLocaleString('fr-FR')}   |   Enseignes3D.fr`, page.w / 2, page.h - 3, { align: 'center' });

  const safe = (meta?.name || 'plan_pose').toString().replace(/[^a-z0-9]/gi, '_');
  const filename = `plan_pose_${safe}_${Date.now()}.pdf`;
  doc.save(filename);
  return {
    filename,
    ratio,
    format: pageFmt.toUpperCase(),
    totalWidthMm: Math.round(totalWidthMm),
    heightMm: Math.round(heightMm),
  };
}


/* ================================================================================================
 * TRUE 1:1 multi-page A4 print — tiled gabarit ready to glue on the wall
 * ================================================================================================
 * Each tile = A4 landscape @ 297×210 mm.
 * Margin around each tile = 10 mm (printer safe zone).
 * Overlap between tiles = 5 mm (for gluing / matching marks).
 * Corner assembly marks (✚) + page code (col letter + row number: A1, A2, B1, …) at each corner
 * of the printable area, so the installer aligns adjacent pages by matching identical codes.
 * ================================================================================================
 */
export async function exportPosePlanTrue1to1(state) {
  const { segments, dimensions, standoffs, meta } = state || {};
  if (!Array.isArray(segments) || segments.length === 0) {
    throw new Error('Aucun segment à tracer');
  }
  const font = await getFont();
  const heightMm = num(dimensions?.height, 150);

  // Per-segment bounds (identical to scaled export)
  const segData = segments.map((seg) => {
    const b = segmentBoundsMm(font, seg?.text, heightMm);
    return { seg: seg || {}, ...b };
  });
  const gapDefault = 15;
  let cursor = 0;
  const positioned = segData.map((sd) => {
    const x = cursor;
    const gap = sd.seg?.detached ? num(sd.seg?.gapAfterMm, 30) : gapDefault;
    cursor += num(sd.width, heightMm) + gap;
    return { ...sd, x };
  });
  const totalWmm = Math.max(1, cursor - gapDefault);

  // ------- Tiling geometry -------
  const A4 = A4_LANDSCAPE;
  // Consumer inkjet/laser printers cannot print to the edge — they have physical
  // dead-zones (typically 3–5 mm on premium models, up to 12–15 mm on cheap ones).
  //   marginMm   = safe zone around the sheet (12 mm covers 95 % of consumer printers)
  //   overlapMm  = wall content shared between neighbouring pages (for gluing)
  // The overlap sits INSIDE the printable area on both pages, so even if the printer
  // clips a millimetre or two of the outer margin, the overlap zone stays intact.
  const marginMm = 12;
  const overlapMm = 8;
  // Printable area per page (safe zone)
  const pageInnerW = A4.w - 2 * marginMm; // 273
  const pageInnerH = A4.h - 2 * marginMm; // 186
  // Effective tile step = inner minus overlap (so pages share `overlapMm` for gluing)
  const stepX = pageInnerW - overlapMm; // 265
  const stepY = pageInnerH - overlapMm; // 178
  // Total envelope with a 5 mm padding around the letters
  const padMm = 5;
  const envW = totalWmm + padMm * 2;
  const envH = heightMm + padMm * 2;
  // Grid of tiles (col × row)
  const cols = Math.max(1, Math.ceil((envW - overlapMm) / stepX));
  const rows = Math.max(1, Math.ceil((envH - overlapMm) / stepY));

  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });

  // Position of the whole envelope in "wall coordinates" (origin = top-left of envelope)
  // For each tile (c,r), its top-left in wall coords = (c*stepX, r*stepY)

  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      if (c + r > 0) doc.addPage('a4', 'landscape');

      // Wall-coord window covered by this tile
      const wxMin = c * stepX;
      const wyMin = r * stepY;
      const wxMax = wxMin + pageInnerW;
      const wyMax = wyMin + pageInnerH;

      // Header — placed INSIDE the safe zone (dead zone of consumer printers can go up to 8 mm).
      const headerH = 6;
      doc.setFillColor(20, 20, 24);
      doc.rect(marginMm, marginMm, pageInnerW, headerH, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(7); doc.setFont('helvetica', 'bold');
      doc.text(`PLAN DE POSE 1:1 — ${meta?.name || 'Enseigne'}`, marginMm + 2, marginMm + 4.2);
      const pageCode = `${String.fromCharCode(65 + c)}${r + 1}`; // A1, B1, A2…
      doc.text(`Page ${pageCode} (col ${c + 1}/${cols}, ligne ${r + 1}/${rows})`, A4.w / 2, marginMm + 4.2, { align: 'center' });
      doc.setFont('helvetica', 'normal');
      doc.text(`${envW.toFixed(0)}×${envH.toFixed(0)} mm · imprimer @100%`, marginMm + pageInnerW - 2, marginMm + 4.2, { align: 'right' });

      // Printable frame — the whole safe zone
      doc.setDrawColor(150);
      doc.setLineWidth(0.2);
      doc.rect(marginMm, marginMm, pageInnerW, pageInnerH, 'S');

      // Corner assembly marks (✚) + shared codes at each corner
      drawCornerMarks(doc, marginMm, pageInnerW, pageInnerH, c, r, cols, rows);

      // Overlap zones — filled light-blue strips (visual match between adjacent pages).
      // Content inside these strips appears on BOTH sides of the join.
      doc.setFillColor(220, 240, 255);
      // Right overlap (this page has a right neighbour): last overlapMm strip of the frame
      if (c < cols - 1) {
        doc.rect(marginMm + pageInnerW - overlapMm, marginMm + headerH, overlapMm, pageInnerH - headerH, 'F');
      }
      // Left overlap (this page has a left neighbour): first overlapMm strip of the frame
      if (c > 0) {
        doc.rect(marginMm, marginMm + headerH, overlapMm, pageInnerH - headerH, 'F');
      }
      // Bottom overlap
      if (r < rows - 1) {
        doc.rect(marginMm, marginMm + pageInnerH - overlapMm, pageInnerW, overlapMm, 'F');
      }
      // Top overlap
      if (r > 0) {
        doc.rect(marginMm, marginMm + headerH, pageInnerW, overlapMm, 'F');
      }
      // Dashed boundaries of the overlap zones for extra visual guidance
      doc.setDrawColor(50, 130, 200);
      doc.setLineWidth(0.25);
      try { doc.setLineDashPattern([1.2, 1.2], 0); } catch { /* ignore */ }
      if (c < cols - 1) doc.line(marginMm + pageInnerW - overlapMm, marginMm + headerH, marginMm + pageInnerW - overlapMm, marginMm + pageInnerH);
      if (c > 0)        doc.line(marginMm + overlapMm,             marginMm + headerH, marginMm + overlapMm,             marginMm + pageInnerH);
      if (r < rows - 1) doc.line(marginMm, marginMm + pageInnerH - overlapMm, marginMm + pageInnerW, marginMm + pageInnerH - overlapMm);
      if (r > 0)        doc.line(marginMm, marginMm + headerH + overlapMm,   marginMm + pageInnerW, marginMm + headerH + overlapMm);
      try { doc.setLineDashPattern([], 0); } catch { /* ignore */ }

      // Fusion labels ("SUPERPOSER >" or "< SUPERPOSER") — ASCII-safe for Helvetica.
      doc.setFontSize(6); doc.setFont('helvetica', 'bold'); doc.setTextColor(30, 100, 180);
      if (c < cols - 1) {
        const nx = marginMm + pageInnerW - overlapMm / 2;
        doc.text('SUPERPOSER >', nx, marginMm + pageInnerH / 2, { align: 'center', angle: 90 });
      }
      if (c > 0) {
        const nx = marginMm + overlapMm / 2;
        doc.text('< SUPERPOSER', nx, marginMm + pageInnerH / 2, { align: 'center', angle: 90 });
      }
      if (r < rows - 1) {
        doc.text('v SUPERPOSER v', marginMm + pageInnerW / 2, marginMm + pageInnerH - overlapMm / 2 + 1.5, { align: 'center' });
      }
      if (r > 0) {
        doc.text('^ SUPERPOSER ^', marginMm + pageInnerW / 2, marginMm + headerH + overlapMm / 2 + 1.5, { align: 'center' });
      }
      doc.setFont('helvetica', 'normal');

      // Helper: transform wall (wx, wy) → page (px, py) — 1:1
      // Wall Y=0 is at TOP of envelope, envY=heightMm+2*padMm is at BOTTOM.
      // Font baseline is at wy = padMm + heightMm (bottom of the drawing).
      // We keep the letters upright (Y-flip already handled in drawPolyline).
      const toPageX = (wx) => marginMm + (wx - wxMin);
      const toPageY = (wy) => marginMm + (wy - wyMin);

      // --- Draw letter outlines that intersect the tile ---
      positioned.forEach((sd, si) => {
        // Segment origin in wall coords: (padMm + sd.x, padMm + heightMm) — baseline bottom
        const segBaselineWx = padMm + sd.x;
        const segBaselineWy = padMm + heightMm;
        // Quick reject: check segment bbox intersection with tile window
        const segMinX = segBaselineWx;
        const segMaxX = segMinX + sd.width;
        const segMinY = padMm; // top of letters
        const segMaxY = padMm + heightMm;
        if (segMaxX < wxMin || segMinX > wxMax || segMaxY < wyMin || segMinY > wyMax) return;

        // For each shape of the segment, draw its outer + holes as clipped polylines
        doc.setDrawColor(0);
        doc.setLineWidth(0.35);
        (sd.shapes || []).forEach((shape) => {
          drawShapeClipped(doc, shape, segBaselineWx, segBaselineWy, toPageX, toPageY,
            wxMin, wxMax, wyMin, wyMax);
        });
      });

      // --- Draw drilling markers that fall within the tile ---
      positioned.forEach((sd, si) => {
        // Custom holes override automatic layout, mm coords already stored per segment
        const custom = Array.isArray(sd.seg?.customHoles) ? sd.seg.customHoles : [];
        const holes = custom.length > 0
          ? custom.map((h) => ({
              xMm: num(h.xMm, 0),
              yMm: num(h.yMm, 0),
              diameterMm: num(h.diameterMm, 6),
            }))
          : balancedHoles(num(standoffs?.count, 2), num(sd.width, 100), heightMm)
              .map((h) => ({ ...h, diameterMm: num(standoffs?.diameter, 6) }));
        holes.forEach((h, idx) => {
          const stDia = num(h.diameterMm, 6);
          const rDraw = Math.max(2, stDia / 2); // 1:1 = real mm radius (min 2 mm for visibility)
          // Wall coords of the hole
          const holeWx = padMm + sd.x + h.xMm;
          const holeWy = padMm + heightMm - h.yMm; // Y-flip: envelope Y is DOWN in wall coords
          if (holeWx < wxMin - rDraw || holeWx > wxMax + rDraw ||
              holeWy < wyMin - rDraw || holeWy > wyMax + rDraw) return;
          const px = toPageX(holeWx);
          const py = toPageY(holeWy);
          if (!isFiniteNum(px) || !isFiniteNum(py)) return;
          doc.setDrawColor(220, 30, 130);
          doc.setFillColor(220, 30, 130);
          doc.setLineWidth(0.5);
          doc.circle(px, py, rDraw, 'FD');
          doc.setLineWidth(0.4);
          doc.line(px - rDraw * 2.5, py, px + rDraw * 2.5, py);
          doc.line(px, py - rDraw * 2.5, px, py + rDraw * 2.5);
          doc.setFontSize(8);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(220, 30, 130);
          doc.text(`P${idx + 1}·seg${si + 1}·Ø${Math.round(stDia)}`, px + rDraw * 2.7, py - 1);
          doc.setFont('helvetica', 'normal');
        });
      });

      // Envelope outline (green dashed) — helps see the boundary of the sign
      const envMinPx = toPageX(padMm);
      const envMinPy = toPageY(padMm);
      const envMaxPx = toPageX(padMm + totalWmm);
      const envMaxPy = toPageY(padMm + heightMm);
      doc.setDrawColor(0, 150, 60);
      doc.setLineWidth(0.35);
      try { doc.setLineDashPattern([1.5, 1.2], 0); } catch { /* ignore */ }
      // Draw only visible envelope edges
      // Left edge
      if (padMm >= wxMin - 0.1 && padMm <= wxMax + 0.1) {
        doc.line(envMinPx, Math.max(marginMm, envMinPy), envMinPx, Math.min(marginMm + pageInnerH, envMaxPy));
      }
      // Right edge
      if (padMm + totalWmm >= wxMin - 0.1 && padMm + totalWmm <= wxMax + 0.1) {
        doc.line(envMaxPx, Math.max(marginMm, envMinPy), envMaxPx, Math.min(marginMm + pageInnerH, envMaxPy));
      }
      // Top edge
      if (padMm >= wyMin - 0.1 && padMm <= wyMax + 0.1) {
        doc.line(Math.max(marginMm, envMinPx), envMinPy, Math.min(marginMm + pageInnerW, envMaxPx), envMinPy);
      }
      // Bottom edge
      if (padMm + heightMm >= wyMin - 0.1 && padMm + heightMm <= wyMax + 0.1) {
        doc.line(Math.max(marginMm, envMinPx), envMaxPy, Math.min(marginMm + pageInnerW, envMaxPx), envMaxPy);
      }
      try { doc.setLineDashPattern([], 0); } catch { /* ignore */ }

      // 1:1 control tick — always present on every page, bottom-right INSIDE the safe zone
      const ctrlLen = 50;
      const ctrlX0 = marginMm + pageInnerW - ctrlLen - 4;
      const ctrlY = marginMm + pageInnerH - 4;
      doc.setDrawColor(0, 130, 40);
      doc.setLineWidth(0.7);
      doc.line(ctrlX0, ctrlY, ctrlX0 + ctrlLen, ctrlY);
      doc.line(ctrlX0, ctrlY - 1.5, ctrlX0, ctrlY + 1.5);
      doc.line(ctrlX0 + ctrlLen, ctrlY - 1.5, ctrlX0 + ctrlLen, ctrlY + 1.5);
      doc.setFontSize(6); doc.setFont('helvetica', 'bold'); doc.setTextColor(0, 130, 40);
      doc.text('50 mm - Contrôle 1:1', ctrlX0, ctrlY - 2.5);
      doc.setFont('helvetica', 'normal');

      // Assembly instructions on first page only (compact footer INSIDE safe zone)
      if (c === 0 && r === 0) {
        doc.setFontSize(6); doc.setTextColor(80, 80, 80);
        const footY = marginMm + pageInnerH - 12;
        doc.text(
          `Imprimer @100% (desactiver "ajuster a la page"). Marge printer 12 mm. Superposer les zones bleues (${overlapMm} mm) entre pages voisines en alignant les contours des lettres, puis coller.`,
          marginMm + 2, footY,
        );
        doc.text(
          `Contrôler avec le trait vert 50 mm avant de reporter au mur. Trous = Ø variable (voir label de chaque trou).`,
          marginMm + 2, footY + 3,
        );
      }
    }
  }

  const safe = (meta?.name || 'plan_1to1').toString().replace(/[^a-z0-9]/gi, '_');
  const filename = `plan_pose_1a1_${safe}_${cols}x${rows}_${Date.now()}.pdf`;
  doc.save(filename);
  return {
    filename,
    ratio: 1,
    format: 'A4',
    cols,
    rows,
    pages: cols * rows,
    totalWidthMm: Math.round(totalWmm),
    heightMm: Math.round(heightMm),
  };
}

// --------- helpers used by 1:1 tiled export ---------

function balancedHoles(count, segWmm, heightMm) {
  const nStand = Math.max(2, count | 0 || 2);
  const marginRatio = 0.12;
  let rows = 1;
  let cols = nStand;
  if (nStand >= 4) { rows = 2; cols = Math.ceil(nStand / 2); }
  const xL = segWmm * marginRatio;
  const xR = segWmm * (1 - marginRatio);
  const xs = [];
  for (let c = 0; c < cols; c++) {
    const t = cols === 1 ? 0.5 : c / (cols - 1);
    xs.push(xL + (xR - xL) * t);
  }
  const ys = rows === 1 ? [heightMm * 0.5] : [heightMm * 0.78, heightMm * 0.22];
  const holes = [];
  outer: for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      holes.push({ xMm: xs[c], yMm: ys[r] });
      if (holes.length >= nStand) break outer;
    }
  }
  return holes;
}

// Draw shape outer + holes at 1:1, clipped to the tile window.
// (Font Y is up; we flip to page Y-down.)
function drawShapeClipped(doc, shape, segBaselineWx, segBaselineWy, toPageX, toPageY,
  wxMin, wxMax, wyMin, wyMax) {
  if (!shape || typeof shape.getPoints !== 'function') return;
  const outer = shape.getPoints(28);
  drawPolylineClipped(doc, outer, segBaselineWx, segBaselineWy, toPageX, toPageY,
    wxMin, wxMax, wyMin, wyMax);
  (Array.isArray(shape.holes) ? shape.holes : []).forEach((h) => {
    if (h && typeof h.getPoints === 'function') {
      drawPolylineClipped(doc, h.getPoints(28), segBaselineWx, segBaselineWy, toPageX, toPageY,
        wxMin, wxMax, wyMin, wyMax);
    }
  });
}

function drawPolylineClipped(doc, points, segBaselineWx, segBaselineWy, toPageX, toPageY,
  wxMin, wxMax, wyMin, wyMax) {
  if (!Array.isArray(points) || points.length < 2) return;
  // Convert font-space points → wall-space (1:1)
  const wall = points
    .map((p) => (p && Number.isFinite(p.x) && Number.isFinite(p.y))
      ? { wx: segBaselineWx + p.x, wy: segBaselineWy - p.y } // Y flip: font up, wall down
      : null)
    .filter(Boolean);
  if (wall.length < 2) return;
  // Segment-by-segment Cohen-Sutherland-lite clip
  for (let i = 1; i < wall.length; i++) {
    const a = wall[i - 1];
    const b = wall[i];
    const clipped = clipSegment(a.wx, a.wy, b.wx, b.wy, wxMin, wyMin, wxMax, wyMax);
    if (clipped) {
      doc.line(toPageX(clipped.x1), toPageY(clipped.y1), toPageX(clipped.x2), toPageY(clipped.y2));
    }
  }
  // Close the loop
  const first = wall[0];
  const last = wall[wall.length - 1];
  if (first !== last) {
    const clipped = clipSegment(last.wx, last.wy, first.wx, first.wy, wxMin, wyMin, wxMax, wyMax);
    if (clipped) {
      doc.line(toPageX(clipped.x1), toPageY(clipped.y1), toPageX(clipped.x2), toPageY(clipped.y2));
    }
  }
}

// Liang-Barsky segment clipper to axis-aligned rect [xmin,ymin,xmax,ymax]
function clipSegment(x1, y1, x2, y2, xmin, ymin, xmax, ymax) {
  let t0 = 0, t1 = 1;
  const dx = x2 - x1, dy = y2 - y1;
  const p = [-dx, dx, -dy, dy];
  const q = [x1 - xmin, xmax - x1, y1 - ymin, ymax - y1];
  for (let i = 0; i < 4; i++) {
    if (p[i] === 0) {
      if (q[i] < 0) return null;
    } else {
      const t = q[i] / p[i];
      if (p[i] < 0) { if (t > t1) return null; if (t > t0) t0 = t; }
      else          { if (t < t0) return null; if (t < t1) t1 = t; }
    }
  }
  return { x1: x1 + t0 * dx, y1: y1 + t0 * dy, x2: x1 + t1 * dx, y2: y1 + t1 * dy };
}

// Corner assembly marks (✚) with code at each corner of the printable area
function drawCornerMarks(doc, m, w, h, c, r, cols, rows) {
  const positions = [
    { x: m, y: m, key: 'TL', vc: c, vr: r },                            // top-left
    { x: m + w, y: m, key: 'TR', vc: c + 1, vr: r },                    // top-right
    { x: m, y: m + h, key: 'BL', vc: c, vr: r + 1 },                    // bottom-left
    { x: m + w, y: m + h, key: 'BR', vc: c + 1, vr: r + 1 },            // bottom-right
  ];
  doc.setDrawColor(200, 30, 30);
  doc.setLineWidth(0.4);
  doc.setFontSize(7);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(200, 30, 30);
  positions.forEach((p) => {
    const L = 4;
    doc.line(p.x - L, p.y, p.x + L, p.y);
    doc.line(p.x, p.y - L, p.x, p.y + L);
    // Small circle marker
    doc.setLineWidth(0.3);
    doc.circle(p.x, p.y, 1.2, 'S');
    doc.setLineWidth(0.4);
    // Corner-vertex code = colLetter (0..cols) + rowNumber (0..rows)
    // Two adjacent pages share the exact same vertex code at their joined corner.
    const colLetter = String.fromCharCode(65 + p.vc);
    const code = `${colLetter}${p.vr}`;
    // Offset label slightly INSIDE the page so it stays inside the printable area
    const ox = p.key.includes('R') ? -6 : 2;
    const oy = p.key.includes('B') ? -1 : 5;
    doc.text(code, p.x + ox, p.y + oy);
  });
  doc.setFont('helvetica', 'normal');
}
